import React, { useEffect, useMemo, useState } from "react";
import { DataGridPremium, GridColDef, GridToolbar, useGridApiRef, GridRowSelectionModel, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-premium";
import { Button, Chip, Tooltip } from "@mui/joy";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import totalLocationData from "../../../../../../data/locationdata";
import CustomInput from "../../../etc/custominput";
import { tableUseStyle } from "../../../etc/tableusestyle";
import {
    API, toastOptions, errorText, EXCLUDED_LOCATIONS, Position,
    todayStr, mmdd, periodText, formatPhone, isActivePosition,
    badgeText, contractStateText, videoConsentText,
    renderBadgeChip, renderContractStateChip, renderVideoConsentChip, openFileLink
} from "./rosterutils";
import PositionEditModal from "./positioneditmodal";
import SendContractModal from "./sendcontractmodal";
import ContractHistoryModal from "./contracthistorymodal";
import PositionVarsModal from "./positionvarsmodal";

//직원명부 — 한 줄 = 직무(staffPositions). 같은 사람 두 직무면 두 줄 + 겸직 태그
//필터는 클라이언트에서 (서버는 직무 전체를 내려줌). 계약서 발송·재알림·취소·이력·개인 변수·파일 열기가 여기서 시작
type StatusFilter = "active" | "left" | "all" | "hidden";   //hidden = 숨김 보기 (isManaged=0 만)
type BadgeFilter = "all" | "first" | "before10" | "expired" | "unsigned";

const CANCEL_MESSAGE = "관리자에 의해 취소되었습니다";

//구버전 서버 응답(isManaged 없음)이면 전부 관리 대상으로 취급
const normalize = (list: any[]): Position[] => (list || []).map((p: any) => ({ ...p, onboarding: p.onboarding || {}, offboarding: p.offboarding || {}, isManaged: p.isManaged === undefined ? 1 : p.isManaged }));

const StaffRoster: React.FC<any> = (props) => {

    const classes = tableUseStyle();
    const apiRef = useGridApiRef();

    const [positions, setPositions] = useState<Position[]>([]);
    const [parts, setParts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const [status, setStatus] = useState<StatusFilter>("active");
    const [branch, setBranch] = useState<string>("all");
    const [badgeFilter, setBadgeFilter] = useState<BadgeFilter>("all");
    const [searchText, setSearchText] = useState<string>(props.initialSearch || "");

    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [rowBusyId, setRowBusyId] = useState<number | null>(null);
    const [videoSending, setVideoSending] = useState(false);

    const [editModal, setEditModal] = useState<{ open: boolean; mode: "edit" | "create"; position: Position | null; userId: number; userName: string }>({ open: false, mode: "edit", position: null, userId: 0, userName: "" });
    const [sendOpen, setSendOpen] = useState(false);
    const [historyTarget, setHistoryTarget] = useState<{ userId: number; name: string } | null>(null);
    const [varsTarget, setVarsTarget] = useState<Position | null>(null);

    const today = todayStr();

    //CRM 지점 목록 (직무 지점 = CRM 호점, 대치는 2층/3층/6층 따로)
    const branchOptions = totalLocationData.filter((each) =>
        props.user && each.academy.includes(props.user.academy) && !EXCLUDED_LOCATIONS.includes(each.english));

    useEffect(() => {
        loadAll();
    }, []);

    const loadAll = async () => {
        try {
            setLoading(true);
            const [r, p]: any[] = await Promise.all([getFetch(`${API}/roster`), getFetch(`${API}/parts`)]);
            if (r.message === "success") {
                setPositions(normalize(r.positions));
            } else {
                toast.error(errorText(r, "직원명부를 불러오지 못했습니다"), toastOptions);
            }
            if (p.message === "success") {
                setParts(p.parts || []);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    //변경 후 명부만 재조회. 사라진 행은 선택에서 제외
    const loadPositions = async () => {
        try {
            setLoading(true);
            const r: any = await getFetch(`${API}/roster`);
            if (r.message === "success") {
                const next: Position[] = normalize(r.positions);
                setPositions(next);
                const ids = new Set(next.map((x) => x.id));
                setSelectionModel((prev) => prev.filter((id) => ids.has(+id)));
            } else {
                toast.error(errorText(r, "직원명부를 불러오지 못했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    //클라이언트 필터
    const visibleRows = useMemo(() => {
        const text = searchText.trim();
        const isNumber = text.length > 0 && !isNaN(+text[0]);
        return positions.filter((p) => {
            const active = isActivePosition(p, today);
            //기본은 관리 대상만. "숨김 보기" 는 숨긴 사람만
            if (status === "hidden") {
                if (p.isManaged === 1) return false;
            } else if (p.isManaged !== 1) {
                return false;
            }
            if (status === "active" && !active) return false;
            if (status === "left" && active) return false;
            if (branch !== "all" && p.branchKey !== branch) return false;
            if (badgeFilter === "first" && !(p.badge && p.badge.type === "first")) return false;
            if (badgeFilter === "before10" && !(p.badge && p.badge.type === "before" && p.badge.days !== null && p.badge.days <= 10)) return false;
            if (badgeFilter === "expired" && !(p.badge && p.badge.type === "expired")) return false;
            if (badgeFilter === "unsigned" && p.contractState !== "pending") return false;
            if (!text) return true;
            if (isNumber) return String(p.phoneNumber || "").replace(/[^0-9]/g, "").includes(text.replace(/[^0-9]/g, ""));
            return String(p.name || "").includes(text);
        });
    }, [positions, status, branch, badgeFilter, searchText, today]);

    const selectedRows = useMemo(() => {
        const ids = new Set(selectionModel.map((id) => +id));
        return positions.filter((p) => ids.has(p.id));
    }, [selectionModel, positions]);

    //===== 행 액션 =====

    const remind = async (row: Position) => {
        if (!row.latestDoc) return;
        if (!window.confirm(`${row.name}에게 서명 재알림을 보낼까요?${row.expired ? " (유효기간이 지나 7일 연장 후 보냅니다)" : ""}`)) return;
        try {
            setRowBusyId(row.id);
            const result: any = await postFetch(`${API}/documents/remind`, { id: row.latestDoc.id });
            if (result.message === "success") {
                toast.success(`재알림 발송 (${result.remindCount}회${result.extended ? ", 유효기간 +7일" : ""})`, toastOptions);
                loadPositions();
            } else {
                toast.error(errorText(result, "재알림에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setRowBusyId(null);
        }
    };

    const cancel = async (row: Position) => {
        if (!row.latestDoc) return;
        if (!window.confirm(`${row.name}의 계약서 서명 요청을 취소할까요? (취소 후 다시 발송할 수 있습니다)`)) return;
        try {
            setRowBusyId(row.id);
            const result: any = await postFetch(`${API}/documents/cancel`, { id: row.latestDoc.id, message: CANCEL_MESSAGE });
            if (result.message === "success") {
                toast.success("요청을 취소했습니다", toastOptions);
                loadPositions();
            } else {
                toast.error(errorText(result, "취소에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setRowBusyId(null);
        }
    };

    //명부 관리 대상 켜기/끄기 — 직무·이력은 삭제하지 않음
    const setManaged = async (row: Position, managed: boolean) => {
        if (!window.confirm(managed ? `${row.name}을(를) 다시 직원명부에서 관리할까요?` : `${row.name}을(를) 직원명부에서 숨길까요? (직무·계약 이력은 지워지지 않고 "숨김 보기"에서 다시 켤 수 있습니다)`)) return;
        try {
            setRowBusyId(row.id);
            const result: any = await postFetch(`${API}/profiles/managed`, { userId: row.userId, managed });
            if (result.message === "success") {
                toast.success(managed ? "다시 관리 대상으로 켰습니다" : "명부에서 숨겼습니다", toastOptions);
                loadPositions();
            } else {
                toast.error(errorText(result, "저장에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setRowBusyId(null);
        }
    };

    //첫 계약 첨부 OCR 다시 읽기 (needsReview 건)
    const reocr = async (row: Position) => {
        if (!window.confirm(`${row.name}의 첫 계약서 첨부(신분증·통장사본)를 다시 읽을까요? 이미 있는 값은 덮어쓰지 않습니다.`)) return;
        try {
            setRowBusyId(row.id);
            const result: any = await postFetch(`${API}/profiles/reocr`, { userId: row.userId });
            if (result.message === "success") {
                const pf = result.profile || {};
                toast[pf.needsReview ? "error" : "success"](pf.needsReview ? `다시 읽었지만 확인 필요: ${pf.reviewMemo || ""}` : "주민번호·계좌를 읽어왔습니다", toastOptions);
                loadPositions();
            } else {
                toast.error(result.message === "noFirstContract" ? "서명완료된 첫 계약서가 없습니다" : errorText(result, "다시 읽기에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setRowBusyId(null);
        }
    };

    const sendVideoConsent = async () => {
        const userIds = Array.from(new Set(selectedRows.map((r) => r.userId)));
        if (userIds.length === 0) return;
        const targets = selectedRows.filter((r) => r.videoConsentStatus === "none");
        const targetIds = Array.from(new Set(targets.map((r) => r.userId)));
        if (targetIds.length === 0) {
            toast.error("체크한 직원 중 영상동의서 미발송인 사람이 없습니다", toastOptions);
            return;
        }
        if (!window.confirm(`${targetIds.length}명에게 영상정보 동의서를 보낼까요? (이미 발송된 사람은 제외)`)) return;
        try {
            setVideoSending(true);
            const result: any = await postFetch(`${API}/videoconsent/send`, { userIds: targetIds });
            if (result.message === "success") {
                const okCount = (result.results || []).filter((r: any) => r.ok).length;
                const fails = (result.results || []).filter((r: any) => !r.ok && !r.skipped);
                toast.success(`영상동의서 발송 ${okCount}명${fails.length ? ` · 실패 ${fails.length}명` : ""}`, toastOptions);
                fails.forEach((f: any) => toast.error(`userId ${f.userId}: ${f.error}`, toastOptions));
                loadPositions();
            } else {
                toast.error(errorText(result, "발송에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setVideoSending(false);
        }
    };

    //엑셀 — DataGridPremium 내장 export (exceljs 는 x-data-grid-premium 의존성으로 이미 설치됨). 실패 시 CSV
    const exportExcel = async () => {
        const fileName = `직원명부_${today}`;
        try {
            await apiRef.current.exportDataAsExcel({ fileName });
        } catch (e) {
            console.log(e);
            apiRef.current.exportDataAsCsv({ fileName, utf8WithBom: true });
        }
    };

    //===== 컬럼 =====

    const columns: GridColDef[] = useMemo(() => [
        {
            field: "badge", headerName: "딱지", width: 120,
            valueGetter: (params: any) => badgeText(params.row.badge),
            renderCell: (params: any) => renderBadgeChip(params.row.badge)
        },
        { field: "name", headerName: "이름", width: 100, cellClassName: "boldcell" },
        { field: "branchLabel", headerName: "지점", width: 90 },
        { field: "partName", headerName: "파트", width: 170, valueGetter: (params: any) => params.row.partName || "" ,
            renderCell: (params: any) => params.row.partName ? params.row.partName : <span style={{ color: "#d32f2f" }}>파트 없음</span> },
        {
            field: "isMultiple", headerName: "겸직", width: 60,
            valueGetter: (params: any) => (params.row.isMultiple ? "겸직" : ""),
            renderCell: (params: any) => (params.row.isMultiple ? <Chip size="sm" variant="soft" color="primary">겸직</Chip> : "")
        },
        { field: "phoneNumber", headerName: "연락처", width: 120, valueFormatter: (params: any) => formatPhone(params.value) },
        {
            field: "residentNumber", headerName: "주민등록번호", width: 260,
            renderCell: (params: any) => (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ color: params.row.residentNumber ? "#111" : "#9ca3af", fontSize: "12px" }}>{params.row.residentNumber || "첫 계약 서명 후 자동"}</span>
                    <Button size="sm" variant="outlined" color="neutral" disabled={!params.row.idCard}
                        onClick={() => openFileLink({ kind: "attachment", documentId: params.row.idCard.documentId, attachmentId: params.row.idCard.attachmentId })}>사진</Button>
                    {/* OCR 이 실패했거나 기존 값과 달랐던 건(needsReview) — 메모를 툴팁으로, 다시 읽기 버튼 */}
                    {!!params.row.needsReview &&
                        <Tooltip title={params.row.reviewMemo || "확인 필요"} placement="top">
                            <span><Button size="sm" variant="soft" color="warning" loading={rowBusyId === params.row.id} onClick={() => reocr(params.row)}>다시 읽기</Button></span>
                        </Tooltip>
                    }
                </div>
            )
        },
        {
            field: "bankAccount", headerName: "계좌", width: 240,
            valueGetter: (params: any) => (params.row.bankAccount ? `${params.row.bankName} ${params.row.bankAccount} (${params.row.accountHolder})` : ""),
            renderCell: (params: any) => (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ color: params.row.bankAccount ? "#111" : "#9ca3af", fontSize: "12px" }}>
                        {params.row.bankAccount ? `${params.row.bankName} ${params.row.bankAccount}${params.row.accountHolder ? ` (${params.row.accountHolder})` : ""}` : "첫 계약 서명 후 자동"}
                    </span>
                    <Button size="sm" variant="outlined" color="neutral" disabled={!params.row.bankbook}
                        onClick={() => openFileLink({ kind: "attachment", documentId: params.row.bankbook.documentId, attachmentId: params.row.bankbook.attachmentId })}>사진</Button>
                </div>
            )
        },
        { field: "startDate", headerName: "입사일", width: 100, valueGetter: (params: any) => params.row.startDate || "" },
        { field: "endDate", headerName: "퇴사일", width: 100, valueGetter: (params: any) => params.row.endDate || "" },
        { field: "workDuration", headerName: "근무기간", width: 120 },
        { field: "employmentInsurance", headerName: "고보", width: 60, valueGetter: (params: any) => params.row.employmentInsurance || "미정" },
        {
            field: "videoConsentStatus", headerName: "영상동의", width: 130,
            valueGetter: (params: any) => videoConsentText(params.row.videoConsentStatus),
            renderCell: (params: any) => (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {renderVideoConsentChip(params.row.videoConsentStatus)}
                    <Button size="sm" variant="outlined" color="neutral" disabled={!params.row.videoConsentDocumentId}
                        onClick={() => openFileLink({ kind: "file", documentId: params.row.videoConsentDocumentId })}>보기</Button>
                </div>
            )
        },
        {
            field: "lastPeriod", headerName: "최근 계약기간", width: 130,
            valueGetter: (params: any) => (params.row.latestDoc ? periodText(params.row.latestDoc.periodStart, params.row.latestDoc.periodEnd) : "없음")
        },
        {
            field: "contractState", headerName: "계약 상태", width: 150,
            valueGetter: (params: any) => contractStateText(params.row),
            renderCell: (params: any) => renderContractStateChip(params.row)
        },
        {
            field: "expiresAt", headerName: "서명 유효기간", width: 120,
            valueGetter: (params: any) => (params.row.contractState === "pending" && params.row.latestDoc ? `${mmdd(params.row.latestDoc.expiresAt)}${params.row.expired ? " 만료됨" : ""}` : "-"),
            renderCell: (params: any) => {
                if (params.row.contractState !== "pending" || !params.row.latestDoc) return "-";
                return params.row.expired
                    ? <span style={{ color: "#d32f2f", fontFamily: "Apple_SB" }}>{mmdd(params.row.latestDoc.expiresAt)} 만료됨</span>
                    : <span>{mmdd(params.row.latestDoc.expiresAt)}</span>;
            }
        },
        {
            field: "remindCount", headerName: "재알림", width: 90,
            valueGetter: (params: any) => `${params.row.latestDoc ? params.row.latestDoc.remindCount : 0}회`,
            renderCell: (params: any) => {
                const log = params.row.latestDoc ? params.row.latestDoc.remindLog || [] : [];
                const title = log.length ? log.map((l: any) => `${mmdd(l.at)} ${l.byName || ""}${l.extended ? " (+7일)" : ""}`).join("\n") : "재알림 기록 없음";
                return <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{title}</div>} placement="top"><span>{params.row.latestDoc ? params.row.latestDoc.remindCount : 0}회</span></Tooltip>;
            }
        },
        {
            field: "lastSent", headerName: "최근 발송", width: 130,
            valueGetter: (params: any) => (params.row.latestDoc ? `${params.row.latestDoc.sentByName} · ${mmdd(params.row.latestDoc.sentAt)}` : "-")
        },
        {
            field: "contractActions", headerName: "계약서", width: 210, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => {
                const row: Position = params.row;
                if (row.contractState === "pending" && row.latestDoc) {
                    return (
                        <div style={{ display: "flex", gap: "6px" }}>
                            <Button size="sm" variant="outlined" color={row.expired ? "warning" : "primary"} loading={rowBusyId === row.id} onClick={() => remind(row)}>
                                {row.expired ? "재알림 (+7일 연장)" : "재알림"}
                            </Button>
                            <Button size="sm" variant="outlined" color="danger" disabled={rowBusyId === row.id} onClick={() => cancel(row)}>취소</Button>
                        </div>
                    );
                }
                if (row.contractState === "completed" && row.latestDoc) {
                    return <Button size="sm" variant="outlined" color="neutral" onClick={() => openFileLink({ kind: "file", documentId: row.latestDoc!.documentId })}>PDF</Button>;
                }
                return "-";
            }
        },
        {
            field: "history", headerName: "이력", width: 90, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => <Button size="sm" variant="plain" onClick={() => setHistoryTarget({ userId: params.row.userId, name: params.row.name })}>전체보기</Button>
        },
        {
            field: "vars", headerName: "변수", width: 70, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => <Button size="sm" variant="plain" disabled={!params.row.partId} onClick={() => setVarsTarget(params.row)}>확인</Button>
        },
        {
            field: "acquisitionReport", headerName: "취득신고", width: 170,
            //신규직원 TODO 와 같은 값 (onboarding.acquisitionInput / acquisitionDone)
            valueGetter: (params: any) => {
                const ob = params.row.onboarding || {};
                const i = ob.acquisitionInput ? `입력 ${mmdd(ob.acquisitionInput.value)}` : "";
                const d = ob.acquisitionDone ? `이행 ${mmdd(ob.acquisitionDone.value)}` : "";
                return [i, d].filter(Boolean).join(" · ");
            }
        },
        { field: "memo", headerName: "비고", width: 160, renderCell: (params: any) => <span title={params.row.memo || ""}>{params.row.memo || ""}</span> },
        {
            field: "edit", headerName: "편집", width: 260, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => (
                <div style={{ display: "flex", gap: "6px" }}>
                    <Button size="sm" variant="outlined" color="neutral" onClick={() => setEditModal({ open: true, mode: "edit", position: params.row, userId: params.row.userId, userName: params.row.name })}>줄 편집</Button>
                    <Button size="sm" variant="plain" color="neutral" onClick={() => setEditModal({ open: true, mode: "create", position: null, userId: params.row.userId, userName: params.row.name })}>파트 추가</Button>
                    {params.row.isManaged === 1
                        ? <Button size="sm" variant="plain" color="danger" loading={rowBusyId === params.row.id} onClick={() => setManaged(params.row, false)}>숨기기</Button>
                        : <Button size="sm" variant="soft" color="primary" loading={rowBusyId === params.row.id} onClick={() => setManaged(params.row, true)}>다시 관리</Button>}
                </div>
            )
        }
    ], [rowBusyId]);

    const toggleSx = { "& .MuiToggleButton-root": { padding: "4px 10px", fontSize: "12px" } };

    return (
        <div>

            {/* 필터 행 */}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "12px" }}>
                <ToggleButtonGroup color="primary" size="small" exclusive value={status} sx={toggleSx} onChange={(e, v) => { if (v) setStatus(v); }}>
                    <ToggleButton value="active">재직</ToggleButton>
                    <ToggleButton value="left">퇴사</ToggleButton>
                    <ToggleButton value="all">전체</ToggleButton>
                    <ToggleButton value="hidden">숨김 보기</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup color="primary" size="small" exclusive value={branch} sx={toggleSx} onChange={(e, v) => { if (v) setBranch(v); }}>
                    <ToggleButton value="all">전체</ToggleButton>
                    {branchOptions.map((b) => (
                        <ToggleButton value={b.english} key={b.english}>{b.korean}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <ToggleButtonGroup color="primary" size="small" exclusive value={badgeFilter} sx={toggleSx} onChange={(e, v) => { if (v) setBadgeFilter(v); }}>
                    <ToggleButton value="all">전체</ToggleButton>
                    <ToggleButton value="first">첫 계약 전</ToggleButton>
                    <ToggleButton value="before10">계약 만료 10일 전</ToggleButton>
                    <ToggleButton value="expired">계약 만료</ToggleButton>
                    <ToggleButton value="unsigned">미서명</ToggleButton>
                </ToggleButtonGroup>
                <div style={{ marginLeft: "auto" }}>
                    <CustomInput value={searchText} onChange={(e: any) => setSearchText(e.target.value)} placeholder="이름 or 전화번호" />
                </div>
            </div>

            {/* 상단 버튼 */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                <Button size="sm" variant="solid" disabled={selectionModel.length === 0} onClick={() => setSendOpen(true)}>
                    체크한 직원 계약서 일괄 발송 ({selectionModel.length}건)
                </Button>
                <Button size="sm" variant="outlined" color="neutral" disabled={selectionModel.length === 0} loading={videoSending} onClick={sendVideoConsent}>영상동의서 발송</Button>
                <Button size="sm" variant="outlined" color="neutral" onClick={exportExcel}>엑셀 다운로드</Button>
                <div style={{ marginLeft: "auto", fontFamily: "Apple_R", fontSize: "12px", color: "#9ca3af" }}>
                    표시 {visibleRows.length}줄 / 전체 {positions.length}줄
                </div>
            </div>

            <div className={classes.root} style={{ height: "680px", width: "100%", backgroundColor: "white" }}>
                <DataGridPremium
                    rows={visibleRows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    apiRef={apiRef}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={(m) => setSelectionModel(m)}
                    isRowSelectable={(params) => isActivePosition(params.row, today)}
                    initialState={{ pinnedColumns: { left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "badge", "name"] } }}
                    slots={{ toolbar: GridToolbar }}
                    getRowHeight={() => 44}
                    sx={{ "& .boldcell": { fontFamily: "Apple_SB" } }}
                />
            </div>

            <div style={{ marginTop: "12px", padding: "12px 16px", background: "#fffbea", border: "1px solid #f5e6a8", borderRadius: "8px", fontFamily: "Apple_R", fontSize: "12px", color: "#6b7280", lineHeight: 1.7 }}>
                <b>읽는 법</b> · 직원은 여기서 추가하지 않음 — 전체학생정보에서 직원 줄 선택 → "신규TODO" 를 누르면 명부에 자동 등록 · 한 사람이 두 파트를 하면 줄이 두 개(겸직 표시) · 계약서는 줄(파트)마다 따로 나감 ·
                <b> 계약 상태</b>는 유캔싸인에서 자동으로 들어옴 · <b>딱지</b> = 첫계약 전 / 만료 N일 전 / 만료 경과 N일 (지난 계약 종료일 기준) · <b>서명 유효기간</b> 지나면 재알림 눌렀을 때 자동으로 7일 연장하고 보냄 ·
                <b> 미서명</b>이 남아 있으면 그 줄은 새 계약서를 못 보내고 <b>재알림</b>만 됨 · 주민번호·계좌는 첫 계약서 첨부(신분증·통장사본)에서 자동으로 읽어오고 "사진" 눌러 원본 확인
            </div>

            <PositionEditModal
                open={editModal.open}
                mode={editModal.mode}
                position={editModal.position}
                userId={editModal.userId}
                userName={editModal.userName}
                branchOptions={branchOptions}
                parts={parts}
                onClose={() => setEditModal((prev) => ({ ...prev, open: false }))}
                onSaved={loadPositions}
            />

            <SendContractModal
                open={sendOpen}
                positionIds={selectionModel.map((id) => +id)}
                user={props.user}
                onClose={(sent: boolean) => { setSendOpen(false); if (sent) { setSelectionModel([]); loadPositions(); } }}
            />

            <ContractHistoryModal
                open={!!historyTarget}
                userId={historyTarget ? historyTarget.userId : 0}
                userName={historyTarget ? historyTarget.name : ""}
                onClose={() => setHistoryTarget(null)}
                onChanged={loadPositions}
            />

            <PositionVarsModal
                open={!!varsTarget}
                position={varsTarget}
                onClose={() => setVarsTarget(null)}
                onSaved={loadPositions}
            />

        </div>
    );
};

export default StaffRoster;
